// PlantBioPage.jsx
// TK 10/29/2k22

// Packages, Dependencies, and Shared Libraries
import React, { useState, useEffect, useCallback } from "react";
import {
  getPlantFromDatabase,
  getSpeciesFromDatabase,
  getUserFromDatabase,
} from "../DatabaseOperations";
import { useParams, useLocation, useNavigate } from "react-router-dom";

import {
  openInNewTab,
  isUserPublic,
  plantHasSpriteArt,
} from "../sharedFunctions.js";

// Constants
import * as constants from "../constants";

// React Components
import BioPageHeader from "./Header/BioPageHeader";
import Loading from "../MiscPages/Loading";
import Body from "./Body/Body";
import Error from "../MiscPages/Error";
import PlantImage from "../Sprite/PlantImage";
import ShareBar from "../SharedComponents/ShareBar";

// Constants
import { bioPlantContainerScale } from "../constants";

// CSS

/*******************************************************************************
 * @func PlantBioPage (React Component)
 * The component that displays the whole bio page for the plant:
 * - Sprite (plant image)
 * - Name and species
 * - Care Instructions
 * - Bio (Prompts/Responses, random facts)
 * - Bio Bubbles
 * - Reporting features
 *
 * @param None (see @notes section)
 * @return JSX Element
 *
 * @notes This component is called when the route /plant/:id is used, where :id
 * is the unique database identifier string. This is passed to the component
 * using the useParams() function. We set it to a state variable upon render of
 * the component.
 *
 ******************************************************************************/
const CTA_BOTTOM_OFFSET_PX = 8;

const getCampaignSessionId = () => {
  try {
    const existing = window.sessionStorage.getItem("campaignSessionId");
    if (existing) return existing;
    const nextId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem("campaignSessionId", nextId);
    return nextId;
  } catch (_) {
    return null;
  }
};

const PlantBioPage = () => {
  // @TODO: Lots of hooks -> could we reduce the number of booleans and instead
  // use a string/enum for a state machine?
  // Hooks
  const [plantID] = useState(useParams().id); // Unique Firebase DB document id string
  const [plant, setPlant] = useState(null); // Plant Object
  const [species, setSpecies] = useState(null); // Species Object
  const [ownerIsPublic, setOwnerIsPublic] = useState(null); // null = still checking; unowned plants resolve to true
  const [hasLoaded, setHasLoaded] = useState(false); // Whether the query promise from DB has returned and plant state is set
  const [isValid, setIsValid] = useState(false); // If the plant is valid (id exists in DB)
  const [error, setError] = useState(""); // Error String display to the user
  const location = useLocation();
  const navigate = useNavigate();
  const isOwned = Boolean(plant?.ownedBy || plant?.owner);

  // Scan tracking for campaign URLs
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const campaign = params.get("campaign");
    const printJobId = params.get("printJob");
    const speciesParam = params.get("species");
    if (!campaign || !plantID) return;

    // Fire-and-forget; backend records with admin privileges
    try {
      fetch(
        "https://us-central1-plants-5f51e.cloudfunctions.net/api/trackScan",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            plantID,
            campaign,
            printJobId,
            species: speciesParam,
            referrer: document.referrer || "",
            userAgent: navigator.userAgent || "",
            path: window.location.pathname + window.location.search,
            sessionId: getCampaignSessionId(),
          }),
        }
      ).catch(() => {});
    } catch (_) {}
  }, [location.search, plantID]);

  // Single persistent sprite - always rendered alongside the bio content
  const persistentSprite = plant && species && (
    <div
      className="sprite-container bio-sprite flex flex-col justify-end"
      style={{ position: "relative", transition: "all 0.3s ease" }}
    >
      <PlantImage
        plant={plant}
        species={species}
        onLoadCallback={(loaded) => {
          setHasLoaded(loaded);
        }}
        scale={bioPlantContainerScale}
      />
    </div>
  );

  const ctaName =
    (plant?.name && plant.name.trim()) || species?.commonName || "This plant";

  // Keep the browser tab title in sync with the plant being viewed. Only
  // named once the plant passes visibility checks (don't leak hidden plants)
  useEffect(() => {
    if (plant && species && isValid) {
      const namePart =
        plant.name && plant.name.trim()
          ? `${plant.name.trim()} the ${species.commonName || plant.species}`
          : species.commonName || plant.species;
      document.title = `${namePart} | Leaflings`;
    }
    return () => {
      document.title = "Leaflings — Your Plants, Brought to Life";
    };
  }, [plant, species, isValid]);

  /*****************************************************************************
   * @func verifySprite
   * Verifies the sprite returned from the DB is valid and cleans the
   * object up to handle any DB descrepencies
   *
   * @param None
   * @return Promise
   * - On Resolve: Void
   * - On Reject: Returns the error string
   *
   * @notes Reject string is specific in the case we want to add error logging.
   * All errors will be cleaned and replaced with a generic error
   *
   ****************************************************************************/
  const verifySprite = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!plant.sprite) {
        reject("Could not find sprite data member. Returned null.");
      }
      if (plant.sprite.plantPotOverlap == null) {
        reject("Could not find plantPotOverlap data member. Returned null.");
      }
      if (!plant.sprite.eyes.imageName) {
        reject("Could not find eyes imageName data member. Returned null.");
      } else {
        if (plant.sprite.eyes.imageName === "") {
          reject("Eyes imageName data member was an empty string.");
        }
      }
      if (!plant.sprite.mouth.imageName) {
        reject("Could not find mouth imageName data member. Returned null.");
      } else {
        if (plant.sprite.mouth.imageName === "") {
          reject("Mouth imageName data member was an empty string.");
        }
      }

      if (!plant.sprite.pot.imageName) {
        reject("Could not find pot imageName data member. Returned null.");
      } else {
        if (plant.sprite.pot.imageName === "") {
          reject("Pot imageName data member was an empty string.");
        }
      }
      resolve();
    });
  }, [plant]);

  /*****************************************************************************
   * @func verifySpecies
   * Verifies the species returned from the DB is valid and cleans the
   * object up to handle any DB descrepencies
   *
   * @param None
   * @return Promise
   * - On Resolve: Void
   * - On Reject: Returns the error string
   *
   * @notes Reject string is specific in the case we want to add error logging.
   * All errors will be cleaned and replaced with a generic error
   *
   ****************************************************************************/
  const verifySpecies = useCallback(() => {
    return new Promise((resolve, reject) => {
      // Check the whole Care Instructions object

      if (!species.careInstructions) {
        reject(
          "Could not find careInstructions data member for species [" +
            plant.species +
            "]. Returned null."
        );
      }

      // Check just the minimum that is needed to display to the user
      if (!species.careInstructions.lightShort) {
        reject(
          "Could not find careInstructions lightShort data member for species" +
            " [" +
            plant.species +
            "]. Returned null."
        );
      } else {
        if (species.careInstructions.lightShort === "") {
          reject(
            "CareInstructions lightShort data member was an empty string for " +
              "the species [" +
              plant.species +
              "]."
          );
        }
      }

      if (!species.careInstructions.waterShort) {
        reject(
          "Could not find careInstructions waterShort data member for species" +
            " [" +
            plant.species +
            "]. Returned null."
        );
      } else {
        if (species.careInstructions.waterShort === "") {
          reject(
            "CareInstructions waterShort data member was an empty string for " +
              "the species [" +
              plant.species +
              "]."
          );
        }
      }

      if (!species.careInstructions.difficulty) {
        reject(
          "Could not find careInstructions difficulty data member for species" +
            " [" +
            plant.species +
            "]. Returned null."
        );
      } else {
        if (species.careInstructions.difficulty === "") {
          reject(
            "CareInstructions difficulty data member was an empty string for " +
              "the species [" +
              plant.species +
              "]."
          );
        }
      }

      // Stage artwork is optional — species without stage art (or with a bad
      // stage index) still render, with the sprite degrading to pot + face.
      // Normalize the shape so downstream components can rely on it.
      if (!Array.isArray(species.stages)) {
        species.stages = [];
      }
      const stage = species.stages[plant.stage];
      if (stage) {
        if (!stage.plantBody) {
          stage.plantBody = { imageName: "" };
        }
        if (stage.plantPotOverlap == null) {
          stage.plantPotOverlap = 0;
        }
        if (!["small", "medium", "large"].includes(stage.size)) {
          stage.size = "medium";
        }
      }

      resolve();
    });
  }, [species, plant]);

  /*****************************************************************************
   * @func verifyPlant
   * Verifies the plant returned from the DB is valid and cleans the
   * object up to handle any DB descrepencies
   *
   * @param None
   * @return Promise
   * - On Resolve: Void
   * - On Reject: Returns the error string
   *
   * @notes Reject string is specific in the case we want to add error logging.
   * All errors will be cleaned and replaced with a generic error
   *
   ****************************************************************************/
  const verifyPlant = useCallback(() => {
    return new Promise((resolve, reject) => {
      // Check there is a stage for the plant
      if (!plant.stage) {
        // Default to 0
        plant.stage = 0;
      }

      console.log(plant);
      // Check Plant name exists and is not empty
      if (!(plant.name === "") && !plant.name) {
        reject("Plant name data member doesn't exist. Returned null.");
      }

      // Check species exists and is not empty
      if (!plant.species) {
        reject("Plant species data member doesn't exist. Returned null.");
      } else {
        if (plant.species === "") {
          reject("Plant species data member is empty string.");
        }
      }

      // Checks if the bio array exists and makes it empty if not
      if (!plant.bio) {
        plant.bio = []; // Make it an empty array so it displays nothing
      }
      // Checks if the bioBubbles array exists and makes it empty if not
      if (!plant.bioBubbles) {
        plant.bioBubbles = []; // Make it an empty array so it displays nothing
      }

      // Verify the sprite object is valid
      verifySprite().then(
        () => {
          // Verify the species object is valid
          verifySpecies().then(
            () => {
              resolve();
            },
            (speciesError) => {
              reject(speciesError);
            }
          );
        },
        (spriteError) => {
          reject(spriteError);
        }
      );
    });
  }, [plant, verifySprite, verifySpecies]);

  // This is for when the error string updates. It sets the appropriate flags so
  // that the error page displays
  useEffect(() => {
    setIsValid(false);
    if (error !== "") {
      setHasLoaded(true);
      // @TODO: Log errors
    }
  }, [error]); // End useEffect

  // This useEffect fires when the species object updates and verifies the
  // plant and species objects are valid. Plants without full sprite art, and
  // plants owned by non-public users, don't get a public page for now.
  useEffect(() => {
    // Check if any are still pending (ownerIsPublic resolves for all plants)
    if (plant && species && ownerIsPublic !== null) {
      if (!plantHasSpriteArt(plant, species)) {
        setError(
          "This Leafling isn't ready for visitors yet — its portrait is still being painted!"
        );
        return;
      }
      if (!ownerIsPublic) {
        setError("This Leafling belongs to a private gardener.");
        return;
      }
      // Verify the plant and species objects returned are valid
      verifyPlant().then(
        () => {
          setIsValid(true);
        },
        (plantError) => {
          setError(plantError);
        }
      );
    }
  }, [species, plant, ownerIsPublic, verifyPlant]); // End useEffect

  //  This useEffect fires upon load and retrieves the plant based on the ID
  // from the URL. If the ID is invalid, then the error will be displayed to the
  // user.
  useEffect(() => {
    if (plantID !== "") {
      // First get the plant object from the Plants Collection
      getPlantFromDatabase(plantID).then(
        (plant) => {
          setPlant(plant);
          // Then get the species of the plant from the Species Collection
          getSpeciesFromDatabase(plant.species).then(
            (species) => {
              setSpecies(species);
            },
            (reason) => {
              setError("Could not get species document: " + reason);
            }
          );
          // Owned plants are only shown when their owner's profile is public
          const ownerId = plant.ownedBy || plant.owner;
          if (ownerId) {
            getUserFromDatabase(ownerId).then(
              (owner) => {
                setOwnerIsPublic(isUserPublic(owner));
              },
              () => {
                setOwnerIsPublic(false); // Owner doc missing — treat as private
              }
            );
          } else {
            setOwnerIsPublic(true); // Unowned plants (QR tags) are showable
          }
        },
        (reason) => {
          setError("Could not get plant document: " + reason);
        }
      );
    } else {
      setError("Plant ID was empty!");
    }
  }, [plantID]); // End useEffect

  return (
    <React.Fragment>
      <BioPageHeader />
      {!hasLoaded ? <Loading /> : null}
      <div
        className="bg-global-bg pt-1 mx-auto max-w-[600px] px-2"
        style={
          hasLoaded
            ? { display: "block", borderColor: "#ffe942" }
            : { display: "none" }
        }
      >
        {isValid ? (
          <React.Fragment>
            <Body
              plant={plant}
              species={species}
              plantID={plantID}
              sharedSprite={persistentSprite}
            />
            {/*<footer className="footer"></footer>*/}
          </React.Fragment>
        ) : (
          <Error error={error /*"Could not load Plant!"*/} />
        )}
        {/* Share this plant + link to the owner's public window */}
        {hasLoaded && isValid && (
          <div className="font-global mt-6 text-center flex items-center justify-center gap-3 flex-wrap">
            <ShareBar
              title={`${ctaName} on Leaflings`}
              text={`Say hi to ${ctaName}, a Leafling you can chat with!`}
              url={`https://reallifeplants.com/plant/${plantID}`}
              label={`Share ${ctaName}`}
            />
            {isOwned && (
              <button
                className="bg-white text-leaf-green-dark font-bold text-sm rounded-full px-5 py-2 shadow-card border border-leaf-green/25 hover:bg-leaf-green-soft transition-colors"
                onClick={() =>
                  navigate("/window/" + (plant.ownedBy || plant.owner))
                }
              >
                See {ctaName}'s window →
              </button>
            )}
          </div>
        )}
        <div className="font-global mt-8 mb-5 text-center mx-auto text-leaf-brown-soft text-sm">
          <div className="line"></div>
          <div>
            Questions?{" "}
            <span
              className="underline cursor-pointer hover:text-leaf-brown transition-colors"
              onClick={() => openInNewTab(constants.INSTAGRAM_URL)}
            >
              DM us on Instagram
            </span>
          </div>
        </div>
        {/* Extra bottom padding so the floating CTA doesn't cover content */}
        <div className="pb-40"></div>
      </div>

      {/* Floating App CTA */}
      {hasLoaded && isValid && plant && species && !isOwned && (
        <button
          type="button"
          onClick={() => openInNewTab(constants.APP_STORE_URL)}
          aria-label={`Download Leaflings to chat with ${ctaName}`}
          className="fixed left-1/2 -translate-x-1/2 w-[calc(100%-1.5rem)] max-w-[520px] bg-leaf-green-dark text-white border-2 border-white rounded-[30px] px-3 py-3 shadow-lift font-global text-left z-30 hover:bg-leaf-green transition-all active:scale-[0.99] flex items-center justify-between"
          style={{
            bottom: `calc(env(safe-area-inset-bottom, 0px) + ${CTA_BOTTOM_OFFSET_PX}px)`,
          }}
        >
          <div className="flex items-center gap-3 flex-1">
            <div className="min-w-[80px] flex flex-col items-center gap-2">
              <div className="w-[80px] h-[80px] min-w-[50px] rounded-full bg-white border border-green-100 flex items-center justify-center">
                <PlantImage
                  plant={plant}
                  species={species}
                  onLoadCallback={() => {}}
                  width="80px"
                  height="80px"
                />
              </div>
              <div className="absolute bg-white text-green-800 text-xs font-semibold px-3 py-1 rounded-full shadow-sm border border-green-100">
                I'm thirsty!!
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white border-l border-b border-green-100 rotate-45"></span>
              </div>
            </div>
            <div>
              <span className="block text-lg font-semibold">
                {ctaName + " can talk."}
              </span>
              <span className="block text-sm mt-1 text-white/90">
                Get care tips, watering reminders, and chat with this plant in
                the Leaflings app.
              </span>
            </div>
          </div>
          <div className="ml-2 w-10 h-10 rounded-full border border-white/70 flex items-center justify-center shrink-0">
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </button>
      )}
    </React.Fragment>
  );
}; // End PlantBioPage

export default PlantBioPage;
